import { getIdToken, useAuth } from '@pooflabs/web';
import { Check, Cloud, HardDrive, Loader2, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { detectGpuModel } from '@/utils/detectGpu';
import {
  AWS_INSTANCE_MAP,
  AWS_INSTANCE_TYPES,
  AWS_REGIONS,
  getTierMeta,
  suggestTierFromVram,
  TIERS,
  VRAM_OPTIONS,
  type GpuTier,
  type ServerType,
} from '@/utils/gpuNetwork';

interface NodeRegistrationFormProps {
  onRegistered?: () => void;
  onCancel?: () => void;
}

interface RegisterResponse {
  nodeId: string;
}

/**
 * Registration writes go through the backend (POST /api/nodes/register) —
 * the gpuNodes policy only allows the treasury vault to create/update
 * records, so the frontend can never write accounting fields directly.
 */
export const NodeRegistrationForm: React.FC<NodeRegistrationFormProps> = ({ onRegistered, onCancel }) => {
  const { user, login } = useAuth();
  const [serverType, setServerType] = useState<ServerType>('self');

  // Self-hosted fields
  const [gpuModel, setGpuModel] = useState('');
  const [vram, setVram] = useState<number | ''>('');
  const [tier, setTier] = useState<GpuTier>('mid');
  const [tierManuallySet, setTierManuallySet] = useState(false);

  // AWS-hosted fields
  const [awsRegion, setAwsRegion] = useState(AWS_REGIONS[0].value);
  const [awsInstanceType, setAwsInstanceType] = useState('');

  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Best-effort GPU auto-detect on mount (self-hosted only) — always editable.
  useEffect(() => {
    const detected = detectGpuModel();
    if (detected) setGpuModel(detected);
  }, []);

  // Auto-suggest tier from VRAM unless the user has manually overridden it.
  useEffect(() => {
    if (tierManuallySet) return;
    if (typeof vram === 'number') {
      setTier(suggestTierFromVram(vram));
    }
  }, [vram, tierManuallySet]);

  const awsInstanceMeta = awsInstanceType ? AWS_INSTANCE_MAP[awsInstanceType] : undefined;

  const canSubmit =
    serverType === 'self'
      ? gpuModel.trim().length > 1 && typeof vram === 'number' && vram > 0
      : !!awsRegion && !!awsInstanceType && !!awsInstanceMeta;

  const resetForm = () => {
    setGpuModel(detectGpuModel() ?? '');
    setVram('');
    setTier('mid');
    setTierManuallySet(false);
    setAwsInstanceType('');
    setLabel('');
  };

  const handleSubmit = async () => {
    if (!user) {
      try {
        await login();
      } catch {
        toast.error('Login failed. Please try again.');
      }
      return;
    }
    if (!canSubmit) {
      toast.error(
        serverType === 'self' ? 'Enter your GPU model and pick a VRAM size first.' : 'Pick a region and instance type first.'
      );
      return;
    }

    setSubmitting(true);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Please reconnect your wallet and try again.');
        return;
      }
      const authApi = createAuthenticatedApiClient(token, user.address);

      const body =
        serverType === 'self'
          ? {
              serverType,
              gpuModel: gpuModel.trim(),
              vram,
              tier,
              ...(label.trim() ? { label: label.trim() } : {}),
            }
          : {
              serverType,
              gpuModel: awsInstanceMeta!.gpuModel,
              vram: awsInstanceMeta!.vram,
              tier: awsInstanceMeta!.tier,
              awsRegion,
              awsInstanceType,
              ...(label.trim() ? { label: label.trim() } : {}),
            };

      const result = await authApi.post<RegisterResponse>('/api/nodes/register', body);
      if (!result?.nodeId) {
        toast.error('Could not register this node. Please try again.');
        return;
      }

      toast.success(`${body.gpuModel} is online — earning t-hive every 30 seconds.`);
      resetForm();
      onRegistered?.();
    } catch (err) {
      console.error(err);
      const message = err instanceof Error && err.message ? err.message : 'Something went wrong registering your node.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-card p-5 sm:p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="w-4 h-4 text-primary" />
        <h3 className="font-extrabold text-lg tracking-tight">Register a node</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Specs are self-reported — no hardware check. Pick how you want to run it.
      </p>

      {/* ── Server type selector ── */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <button
          type="button"
          onClick={() => setServerType('self')}
          className={`relative text-left rounded-2xl border p-4 transition-all duration-300 ${
            serverType === 'self'
              ? 'border-primary bg-accent shadow-md -translate-y-0.5'
              : 'border-border bg-background hover:border-primary/40 hover:-translate-y-0.5'
          }`}
        >
          {serverType === 'self' && (
            <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
              <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />
            </span>
          )}
          <HardDrive className="w-4 h-4 text-primary mb-1.5" />
          <p className="text-sm font-extrabold">Self-hosted</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Use your own machine as a server</p>
        </button>
        <button
          type="button"
          onClick={() => setServerType('aws')}
          className={`relative text-left rounded-2xl border p-4 transition-all duration-300 ${
            serverType === 'aws'
              ? 'border-primary bg-accent shadow-md -translate-y-0.5'
              : 'border-border bg-background hover:border-primary/40 hover:-translate-y-0.5'
          }`}
        >
          {serverType === 'aws' && (
            <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
              <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />
            </span>
          )}
          <Cloud className="w-4 h-4 text-primary mb-1.5" />
          <p className="text-sm font-extrabold">AWS-hosted</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Run it on an instance instead</p>
        </button>
      </div>

      {serverType === 'self' ? (
        <>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
                GPU model
              </label>
              <input
                value={gpuModel}
                onChange={(e) => setGpuModel(e.target.value)}
                placeholder="e.g. RTX 4090"
                className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
                VRAM
              </label>
              <select
                value={vram}
                onChange={(e) => setVram(e.target.value ? Number(e.target.value) : '')}
                className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select VRAM</option>
                {VRAM_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v} GB
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Tier</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-2">
            {TIERS.map((t) => {
              const active = t.key === tier;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => {
                    setTier(t.key);
                    setTierManuallySet(true);
                  }}
                  className={`relative text-left rounded-2xl border p-3.5 transition-all duration-300 ${
                    active
                      ? 'border-primary bg-accent shadow-md -translate-y-0.5'
                      : 'border-border bg-background hover:border-primary/40 hover:-translate-y-0.5'
                  }`}
                >
                  {active && (
                    <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />
                    </span>
                  )}
                  <p className="text-sm font-extrabold">{t.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{t.vramRange}</p>
                  <p className="text-sm font-bold text-primary mt-2">{t.ratePer30s}/30s</p>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground mb-5">{getTierMeta(tier).blurb}</p>

          <div className="rounded-2xl bg-secondary/60 px-4 py-3 text-[12px] text-muted-foreground mb-5">
            Your server earns while it's on. Turn your computer off and earning pauses — come back and hit{' '}
            <span className="font-semibold text-foreground">Resume</span>.
          </div>
        </>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
                Region
              </label>
              <select
                value={awsRegion}
                onChange={(e) => setAwsRegion(e.target.value)}
                className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              >
                {AWS_REGIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
                Instance type
              </label>
              <select
                value={awsInstanceType}
                onChange={(e) => setAwsInstanceType(e.target.value)}
                className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select instance type</option>
                {AWS_INSTANCE_TYPES.map((it) => (
                  <option key={it} value={it}>
                    {it}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {awsInstanceMeta && (
            <div className="grid grid-cols-3 gap-2.5 mb-5">
              <div className="rounded-2xl bg-secondary/60 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">GPU</p>
                <p className="text-sm font-extrabold mt-0.5">{awsInstanceMeta.gpuModel}</p>
              </div>
              <div className="rounded-2xl bg-secondary/60 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">VRAM</p>
                <p className="text-sm font-extrabold mt-0.5">{awsInstanceMeta.vram} GB</p>
              </div>
              <div className="rounded-2xl bg-secondary/60 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Tier</p>
                <p className="text-sm font-extrabold mt-0.5">{getTierMeta(awsInstanceMeta.tier).label}</p>
              </div>
            </div>
          )}

          <div className="rounded-2xl bg-secondary/60 px-4 py-3 text-[12px] text-muted-foreground mb-5">
            We don't need your AWS keys — just your instance details.
          </div>
        </>
      )}

      <div className="mb-5">
        <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
          Label <span className="text-muted-foreground/60 font-normal">(optional)</span>
        </label>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Home rig, us-east box"
          className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="flex items-center gap-2.5">
        <button
          onClick={handleSubmit}
          disabled={submitting || (!!user && !canSubmit)}
          className="flex-1 rounded-full bg-[#00C805] text-white font-bold py-3.5 shadow-lg shadow-[#00C805]/25 hover:bg-[#00b004] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {!user ? 'Connect wallet to register' : submitting ? 'Registering…' : 'Register node'}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={submitting}
            className="rounded-full border border-input px-5 py-3.5 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default NodeRegistrationForm;
